import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { AlphaBadge } from '../components/AlphaNotice.js';
import { Link, useSearchParams } from 'react-router-dom';
import {
  GAME_TYPES,
  gameLabel,
  type GameType,
  type StatsWindow,
} from '@tv-casino/shared';
import { SparkLegend, Sparkline } from '../components/Sparkline.js';
import sparkStyles from '../components/Sparkline.module.css';
import { WindowPicker } from '../components/WindowPicker.js';
import {
  fetchAdminCasinos,
  fetchAdminLive,
  fetchAdminMarket,
  fetchAdminOverview,
  fetchAdminPlayers,
  fetchAdminRounds,
  fetchAdminUsers,
  fetchIsAdmin,
  setAdmin,
  type AdminCasinoRow,
  type AdminLiveRoom,
  type AdminMarket,
  type AdminMarketMover,
  type AdminMarketPrint,
  type AdminOverview,
  type AdminPlayerRow,
  type AdminRoundRow,
  type AdminUserRow,
} from '../lib/adminApi.js';
import { formatSignedChips } from '@tv-casino/viewmodels/lib/format.js';
import { getSession, type SessionUser } from '../lib/session.js';
import {
  ADMIN_TABS,
  activityBars,
  breadthShares,
  cumulative,
  dayLabel,
  flowRows,
  houseBars,
  houseHold,
  houseResult,
  lineHeights,
  moverModel,
  parseTab,
  timeAgo,
  type AdminTab,
} from '@tv-casino/viewmodels/routes/adminModel.js';
import styles from './Admin.module.css';

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');
const n = (v: number) => v.toLocaleString('en-US');
const netClass = (v: number) => (v > 0 ? styles.up : v < 0 ? styles.down : undefined);
const toneClass = (tone: 'up' | 'down' | 'flat') => (tone === 'up' ? styles.up : tone === 'down' ? styles.down : undefined);
/** The game's colour, shared with the member desk's sparklines; read as `currentColor`. */
const gameTone = (game: GameType) => sparkStyles[`tone_${game}`];

/** Loads once per mount and on demand; errors surface as text, never a blank tab. */
function useLoad<T>(load: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    setError(null);
    load()
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Something went wrong.'));
  }, [load]);
  useEffect(reload, [reload]);
  return { data, error, reload, setData };
}

function Status({ error, loading }: { error: string | null; loading: boolean }) {
  if (error) return <p className={styles.error}>{error}</p>;
  if (loading) return <p className={styles.hint}>Loading…</p>;
  return null;
}

function Tile({
  label,
  value,
  note,
  tone,
  children,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: string;
  children?: ReactNode;
}) {
  return (
    <div className={styles.tile}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={cx(styles.tileValue, tone)}>{value}</span>
      {note && <span className={styles.tileNote}>{note}</span>}
      {children}
    </div>
  );
}

/** A tile's 30-day trend: one gold line, no axes — the tile's number is the read. */
function MiniLine({ values, label }: { values: readonly number[]; label: string }) {
  if (values.length < 2 || values.every((v) => v === values[0])) return null;
  const heights = lineHeights(values);
  const last = values.length - 1;
  return (
    <svg className={styles.miniLine} viewBox="0 0 100 24" preserveAspectRatio="none" role="img" aria-label={label}>
      <polyline
        points={heights.map((h, i) => `${(i / last) * 100},${2 + (1 - h) * 20}`).join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function GameLegend({ games }: { games: readonly GameType[] }) {
  if (games.length < 2) return null;
  return (
    <ul className={styles.legend} aria-label="Games">
      {games.map((g) => (
        <li key={g}>
          <span className={cx(styles.swatch, gameTone(g))} aria-hidden />
          {gameLabel(g)}
        </li>
      ))}
    </ul>
  );
}

/** First, last and weekly labels — a weekly one too close to the last is dropped so they never collide. */
const showAxis = (i: number, count: number) => i === 0 || i === count - 1 || (i % 7 === 0 && count - 1 - i >= 4);

function ActivityChart({ overview }: { overview: AdminOverview }) {
  const { bars, peak, total, games } = activityBars(overview.daily);
  return (
    <section className={styles.panel} aria-labelledby="activity">
      <header className={styles.panelHead}>
        <h2 id="activity" className={styles.panelTitle}>
          Hands played per day
        </h2>
        <span className={styles.hint}>
          Last 30 days · {n(total)} hands · peak {n(peak)}
        </span>
      </header>
      {total === 0 ? (
        <p className={styles.hint}>No hands settled in the last 30 days.</p>
      ) : (
        <>
          <GameLegend games={games} />
          <div className={styles.chart} role="img" aria-label={`Hands per day by game, last 30 days, ${n(total)} total`}>
            {bars.map((bar, i) => (
              <div key={bar.day} className={styles.barSlot} tabIndex={0}>
                <span className={styles.stack} style={{ height: `${Math.max(bar.share * 100, bar.hands ? 2 : 0)}%` }}>
                  {bar.segments.map((s) => (
                    <span key={s.game} className={cx(styles.seg, gameTone(s.game))} style={{ flexGrow: s.hands }} />
                  ))}
                </span>
                <span className={styles.barTip} role="tooltip">
                  <b>{bar.label}</b>
                  <br />
                  {n(bar.hands)} hands · {n(bar.players)} players
                  {bar.segments.map((s) => (
                    <Fragment key={s.game}>
                      <br />
                      <span className={cx(styles.swatch, gameTone(s.game))} aria-hidden />
                      {gameLabel(s.game)} {n(s.hands)}
                    </Fragment>
                  ))}
                </span>
                {showAxis(i, bars.length) && <span className={styles.barAxis}>{bar.label}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * A 30-day line with a hover crosshair: the line is drawn once in SVG, and a
 * grid of day slots on top carries the dot, the rule and the tooltip.
 */
function DayLineChart({
  id,
  title,
  hint,
  days,
  values,
}: {
  id: string;
  title: string;
  hint: string;
  days: readonly { day: string }[];
  values: readonly number[];
}) {
  const heights = lineHeights(values);
  const count = values.length;
  const points = heights.map((h, i) => `${((i + 0.5) / count) * 100},${(1 - h) * 100}`).join(' ');
  return (
    <section className={styles.panel} aria-labelledby={id}>
      <header className={styles.panelHead}>
        <h2 id={id} className={styles.panelTitle}>
          {title}
        </h2>
        <span className={styles.hint}>{hint}</span>
      </header>
      <div className={styles.lineWrap}>
        <span className={styles.yMax}>{n(Math.max(...values))}</span>
        <span className={styles.yMin}>{n(Math.min(...values))}</span>
        <svg className={styles.lineSvg} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
          <polygon
            className={styles.lineArea}
            points={`${(0.5 / count) * 100},100 ${points} ${((count - 0.5) / count) * 100},100`}
          />
          <polyline className={styles.linePath} points={points} vectorEffect="non-scaling-stroke" />
        </svg>
        <div className={styles.chart} role="img" aria-label={`${title}, last 30 days, now ${n(values[count - 1] ?? 0)}`}>
          {days.map((d, i) => (
            <div key={d.day} className={styles.barSlot} tabIndex={0}>
              <span className={styles.rule} />
              <span className={styles.dotMark} style={{ bottom: `${heights[i]! * 100}%` }} />
              <span className={styles.barTip} role="tooltip">
                <b>{dayLabel(d.day)}</b>
                <br />
                {n(values[i]!)}
              </span>
              {showAxis(i, count) && <span className={styles.barAxis}>{dayLabel(d.day)}</span>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OverviewTab() {
  const { data, error } = useLoad(fetchAdminOverview);
  if (!data) return <Status error={error} loading />;
  const house = houseResult(data.hands.net);
  const played = data.games.filter((g) => g.hands > 0);
  return (
    <>
      <div className={styles.tiles}>
        <Tile label="Users" value={n(data.users.total)} note={`+${n(data.users.new7d)} this week · ${n(data.users.admins)} admin${data.users.admins === 1 ? '' : 's'}`} />
        <Tile label="Players" value={n(data.players.total)} note={`${n(data.players.active24h)} seen today · ${n(data.players.active7d)} this week`}>
          <MiniLine values={data.daily.map((d) => d.players)} label="Players per day, last 30 days" />
        </Tile>
        <Tile label="Casinos" value={n(data.casinos.total)} note={`+${n(data.casinos.new7d)} this week`} />
        <Tile label="Rounds" value={n(data.rounds.total)} note={`${n(data.rounds.last24h)} today · ${n(data.rounds.last7d)} this week`} />
        <Tile label="Hands" value={n(data.hands.total)} note={`${n(data.hands.wagered)} chips wagered`}>
          <MiniLine values={data.daily.map((d) => d.wagered)} label="Chips wagered per day, last 30 days" />
        </Tile>
        <Tile label="House result" value={house.label.replace('House ', '')} tone={toneClass(house.tone)} note="Across every settled hand">
          <MiniLine values={cumulative(data.daily.map((d) => 0 - d.net))} label="House result, running, last 30 days" />
        </Tile>
        <Tile
          label="Live now"
          value={n(data.live.playersConnected)}
          note={`players · ${n(data.live.rooms)} rooms · ${n(data.live.tvs)} TVs`}
        />
      </div>

      <ActivityChart overview={data} />

      <section className={styles.panel} aria-labelledby="games">
        <header className={styles.panelHead}>
          <h2 id="games" className={styles.panelTitle}>
            What people are playing
          </h2>
        </header>
        {played.length > 0 && (
          <div className={styles.mix} role="img" aria-label="Share of hands by game">
            {played.map((g) => (
              <span
                key={g.gameType}
                className={cx(styles.mixSeg, gameTone(g.gameType))}
                style={{ flexGrow: g.hands }}
                title={`${gameLabel(g.gameType)} ${Math.round((g.hands / data.hands.total) * 100)}%`}
              />
            ))}
          </div>
        )}
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Game</th>
                <th className={styles.num}>Hands</th>
                <th className={styles.share}>Share</th>
                <th className={styles.num}>Players</th>
                <th className={styles.num}>Wagered</th>
                <th className={styles.num}>Players net</th>
                <th>Last played</th>
              </tr>
            </thead>
            <tbody>
              {data.games.map((g) => {
                const share = data.hands.total ? g.hands / data.hands.total : 0;
                return (
                  <tr key={g.gameType}>
                    <td>
                      <span className={cx(styles.swatch, gameTone(g.gameType))} aria-hidden />
                      {gameLabel(g.gameType)}
                    </td>
                    <td className={styles.num}>{n(g.hands)}</td>
                    <td className={styles.share}>
                      <span className={styles.shareTrack}>
                        <span className={cx(styles.shareFill, gameTone(g.gameType))} style={{ width: `${share * 100}%` }} />
                      </span>
                      <span className={styles.shareText}>{Math.round(share * 100)}%</span>
                    </td>
                    <td className={styles.num}>{n(g.players)}</td>
                    <td className={styles.num}>{n(g.wagered)}</td>
                    <td className={cx(styles.num, netClass(g.net))}>{formatSignedChips(g.net)}</td>
                    <td>{timeAgo(g.lastPlayedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function PlayersTab({ onHistory }: { onHistory: (p: AdminPlayerRow) => void }) {
  const { data, error } = useLoad(fetchAdminPlayers);
  const [filter, setFilter] = useState('');
  if (!data) return <Status error={error} loading />;
  const q = filter.trim().toLowerCase();
  const rows = q
    ? data.filter((p) => p.name.toLowerCase().includes(q) || p.casinoName.toLowerCase().includes(q))
    : data;
  return (
    <section className={styles.panel} aria-label="Players">
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Players ({n(data.length)})</h2>
        <input
          className={styles.search}
          placeholder="Filter by name or casino"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter players"
        />
      </header>
      {rows.length === 0 ? (
        <p className={styles.hint}>No players yet.</p>
      ) : (
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Casino</th>
                <th>Games</th>
                <th className={styles.num}>Hands</th>
                <th className={styles.num}>Wagered</th>
                <th className={styles.num}>Net</th>
                <th className={styles.num}>Chips</th>
                <th>Last seen</th>
                <th>Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={`${p.casinoId}:${p.id}`}>
                  <td>
                    {p.name}
                    {p.host && <span className={styles.badge}>Owner</span>}
                  </td>
                  <td>{p.casinoName}</td>
                  <td>{p.games.length ? p.games.map(gameLabel).join(', ') : <span className={styles.hint}>—</span>}</td>
                  <td className={styles.num}>{n(p.hands)}</td>
                  <td className={styles.num}>{n(p.wagered)}</td>
                  <td className={cx(styles.num, netClass(p.net))}>{formatSignedChips(p.net)}</td>
                  <td className={styles.num}>{n(p.chips)}</td>
                  <td>{timeAgo(p.lastSeenAt)}</td>
                  <td>{timeAgo(p.createdAt)}</td>
                  <td>
                    <button type="button" className={styles.small} onClick={() => onHistory(p)} disabled={p.hands === 0}>
                      History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const PAGE = 50;

interface RoundFilter {
  casinoId: string;
  memberId: string;
  memberLabel: string;
  gameType: string;
}

function RoundsTab({ filter, setFilter }: { filter: RoundFilter; setFilter: (f: RoundFilter) => void }) {
  const casinos = useLoad(fetchAdminCasinos).data;
  const [entries, setEntries] = useState<AdminRoundRow[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(
    (offset: number) => {
      setError(null);
      fetchAdminRounds({
        casinoId: filter.casinoId,
        memberId: filter.memberId,
        gameType: filter.gameType,
        limit: PAGE,
        offset,
      })
        .then((res) => {
          setEntries((prev) => (offset === 0 ? res.entries : [...(prev ?? []), ...res.entries]));
          setHasMore(res.hasMore);
        })
        .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Something went wrong.'));
    },
    [filter.casinoId, filter.memberId, filter.gameType],
  );
  useEffect(() => {
    setEntries(null);
    load(0);
  }, [load]);

  return (
    <section className={styles.panel} aria-label="Game history">
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Game history</h2>
        <div className={styles.filters}>
          <select
            aria-label="Game"
            value={filter.gameType}
            onChange={(e) => setFilter({ ...filter, gameType: e.target.value })}
          >
            <option value="">All games</option>
            {GAME_TYPES.map((g) => (
              <option key={g} value={g}>
                {gameLabel(g)}
              </option>
            ))}
          </select>
          <select
            aria-label="Casino"
            value={filter.casinoId}
            onChange={(e) => setFilter({ ...filter, casinoId: e.target.value, memberId: '', memberLabel: '' })}
          >
            <option value="">All casinos</option>
            {(casinos ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {filter.memberId && (
            <button
              type="button"
              className={styles.chipFilter}
              onClick={() => setFilter({ ...filter, memberId: '', memberLabel: '' })}
            >
              {filter.memberLabel} ✕
            </button>
          )}
        </div>
      </header>
      {error && <p className={styles.error}>{error}</p>}
      {!entries ? (
        !error && <p className={styles.hint}>Loading…</p>
      ) : entries.length === 0 ? (
        <p className={styles.hint}>No rounds match.</p>
      ) : (
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>When</th>
                <th>Player</th>
                <th>Casino</th>
                <th>Game</th>
                <th>Outcome</th>
                <th className={styles.num}>Stake</th>
                <th className={styles.num}>Net</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((r) => (
                <Fragment key={r.id}>
                  <tr className={styles.clickable} onClick={() => setOpen(open === r.id ? null : r.id)}>
                    <td title={r.at}>{timeAgo(r.at)}</td>
                    <td>{r.memberName}</td>
                    <td>{r.casinoName}</td>
                    <td>{gameLabel(r.gameType as GameType)}</td>
                    <td>
                      <span className={cx(styles.outcome, netClass(r.net))}>{r.outcome}</span>
                    </td>
                    <td className={styles.num}>{n(r.stake)}</td>
                    <td className={cx(styles.num, netClass(r.net))}>{formatSignedChips(r.net)}</td>
                    <td className={styles.hint}>{r.note ?? (open === r.id ? '▲' : '▼')}</td>
                  </tr>
                  {open === r.id && (
                    <tr className={styles.detailRow}>
                      <td colSpan={8}>
                        <div className={styles.detail}>
                          <div>
                            <b>Bets</b>
                            <ul>
                              {r.bets.map((b, i) => (
                                <li key={i}>
                                  {b.label}: {n(b.stake)} → {n(b.payout)}
                                </li>
                              ))}
                            </ul>
                          </div>
                          {r.cards.length > 0 && (
                            <div>
                              <b>Cards</b>
                              <ul>
                                {r.cards.map((c, i) => (
                                  <li key={i}>
                                    {c.owner}: {c.cards.map((card) => `${card.rank}${card.suit}`).join(' ')}
                                    {c.label ? ` (${c.label})` : ''}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className={styles.hint}>
                            {new Date(r.at).toLocaleString()} · seat {r.seatIndex + 1} · round {r.roundId.slice(0, 8)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && entries && (
        <button type="button" className={styles.small} onClick={() => load(entries.length)}>
          Load more
        </button>
      )}
    </section>
  );
}

function UsersTab({ me }: { me: SessionUser }) {
  const { data, error, setData } = useLoad(fetchAdminUsers);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  if (!data) return <Status error={error} loading />;

  const toggle = async (user: AdminUserRow) => {
    setBusy(user.id);
    setActionError(null);
    try {
      const updated = await setAdmin(user.id, !user.isAdmin);
      setData(data.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className={styles.panel} aria-label="Users">
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Users ({n(data.length)})</h2>
        <span className={styles.hint}>Accounts that signed up to run a casino. Guests play without one.</span>
      </header>
      {actionError && <p className={styles.error}>{actionError}</p>}
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Player name</th>
              <th className={styles.num}>Casinos</th>
              <th>Signed up</th>
              <th>Last active</th>
              <th>Admin</th>
            </tr>
          </thead>
          <tbody>
            {data.map((u) => {
              const locked = u.bakedAdmin || (u.id === me.id && u.isAdmin);
              return (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{u.playerName ?? <span className={styles.hint}>—</span>}</td>
                  <td className={styles.num}>{n(u.casinos)}</td>
                  <td>{timeAgo(u.createdAt)}</td>
                  <td>{timeAgo(u.lastActiveAt)}</td>
                  <td>
                    <label className={styles.toggle} title={u.bakedAdmin ? 'Built-in admin' : undefined}>
                      <input
                        type="checkbox"
                        checked={u.isAdmin}
                        disabled={locked || busy === u.id}
                        onChange={() => void toggle(u)}
                        aria-label={`Admin: ${u.email}`}
                      />
                      <span>{u.bakedAdmin ? 'Built-in' : u.isAdmin ? 'Admin' : 'No'}</span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CasinosTab({ onHistory }: { onHistory: (c: AdminCasinoRow) => void }) {
  const { data, error } = useLoad(fetchAdminCasinos);
  if (!data) return <Status error={error} loading />;
  return (
    <section className={styles.panel} aria-label="Casinos">
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Casinos ({n(data.length)})</h2>
      </header>
      {data.length === 0 ? (
        <p className={styles.hint}>No casinos yet.</p>
      ) : (
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Owner</th>
                <th className={styles.num}>Tables</th>
                <th className={styles.num}>Players</th>
                <th className={styles.num}>Hands</th>
                <th className={styles.num}>Wagered</th>
                <th>Last hand</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.name}
                    {c.liveTables > 0 && <span className={cx(styles.badge, styles.liveBadge)}>Live {c.liveTables}</span>}
                  </td>
                  <td className={styles.mono}>{c.joinCode}</td>
                  <td>{c.ownerEmail}</td>
                  <td className={styles.num}>{n(c.tables)}</td>
                  <td className={styles.num}>{n(c.players)}</td>
                  <td className={styles.num}>{n(c.hands)}</td>
                  <td className={styles.num}>{n(c.wagered)}</td>
                  <td>{timeAgo(c.lastPlayedAt)}</td>
                  <td>{timeAgo(c.createdAt)}</td>
                  <td>
                    <button type="button" className={styles.small} onClick={() => onHistory(c)} disabled={c.hands === 0}>
                      History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function LiveTab() {
  const { data, error, reload } = useLoad<AdminLiveRoom[]>(fetchAdminLive);
  useEffect(() => {
    const t = window.setInterval(reload, 10_000);
    return () => window.clearInterval(t);
  }, [reload]);
  if (!data) return <Status error={error} loading />;
  return (
    <section className={styles.panel} aria-label="Live now">
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>Live rooms ({n(data.length)})</h2>
        <span className={styles.hint}>In memory on this server · refreshes every 10s</span>
      </header>
      {data.length === 0 ? (
        <p className={styles.hint}>Nobody is at a table right now.</p>
      ) : (
        <div className={styles.rooms}>
          {data.map((room) => (
            <article key={room.code} className={styles.room}>
              <header className={styles.roomHead}>
                <span className={styles.mono}>{room.code}</span>
                <span>{gameLabel(room.gameType)}</span>
                <span className={styles.badge}>{room.phase}</span>
              </header>
              <p className={styles.hint}>
                {room.casinoName ?? 'Quick table'} · round {room.round} · {room.tv ? 'TV on' : 'no TV'} · opened{' '}
                {timeAgo(room.createdAt)}
              </p>
              {room.players.length === 0 ? (
                <p className={styles.hint}>No players.</p>
              ) : (
                <ul className={styles.roomPlayers}>
                  {room.players.map((p, i) => (
                    <li key={i}>
                      <span className={cx(styles.dot, p.connected && styles.dotOn)} aria-label={p.connected ? 'connected' : 'away'} />
                      {p.name} <span className={styles.hint}>{n(p.chips)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

/** Diverging bars: the house up above the baseline, down below it. */
function HouseChart({ days }: { days: AdminMarket['daily'] }) {
  const { bars, total } = houseBars(days);
  const result = houseResult(0 - total);
  return (
    <section className={styles.panel} aria-labelledby="house-days">
      <header className={styles.panelHead}>
        <h2 id="house-days" className={styles.panelTitle}>
          House result per day
        </h2>
        <span className={styles.hint}>Last 30 days · {result.label}</span>
      </header>
      {bars.every((b) => b.house === 0) ? (
        <p className={styles.hint}>No results in the last 30 days.</p>
      ) : (
        <div className={cx(styles.chart, styles.diverge)} role="img" aria-label={`House result per day, ${result.label}`}>
          {bars.map((bar, i) => (
            <div key={bar.day} className={styles.barSlot} tabIndex={0}>
              <span className={styles.halfUp}>
                {bar.share > 0 && <span className={cx(styles.bar, styles.barUp)} style={{ height: `${Math.max(bar.share * 100, 3)}%` }} />}
              </span>
              <span className={styles.halfDown}>
                {bar.share < 0 && <span className={cx(styles.bar, styles.barDown)} style={{ height: `${Math.max(-bar.share * 100, 3)}%` }} />}
              </span>
              <span className={styles.barTip} role="tooltip">
                <b>{bar.label}</b>
                <br />
                {houseResult(0 - bar.house).label} · {n(days[i]!.wagered)} wagered
              </span>
              {showAxis(i, bars.length) && <span className={styles.barAxis}>{bar.label}</span>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FlowPanel({ market }: { market: AdminMarket }) {
  const rows = flowRows(market.flows);
  return (
    <section className={styles.panel} aria-labelledby="flows">
      <header className={styles.panelHead}>
        <h2 id="flows" className={styles.panelTitle}>
          Money flow
        </h2>
        <span className={styles.hint}>Every ledger row, by kind</span>
      </header>
      {rows.length === 0 ? (
        <p className={styles.hint}>No chips have moved yet.</p>
      ) : (
        <ul className={styles.flows}>
          {rows.map((r) => (
            <li key={r.kind} className={styles.flow}>
              <span>{r.label}</span>
              <span className={styles.flowTrack}>
                <span className={cx(styles.flowFill, r.total < 0 ? styles.fillDown : styles.fillUp)} style={{ width: `${r.share * 100}%` }} />
              </span>
              <span className={cx(styles.num, netClass(r.total))}>{r.totalLabel}</span>
              <span className={cx(styles.num, styles.hint)}>{n(r.count)}×</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function MoverList({ title, movers, empty }: { title: string; movers: AdminMarketMover[]; empty: string }) {
  return (
    <section className={styles.panel} aria-label={title}>
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>{title}</h2>
      </header>
      {movers.length === 0 ? (
        <p className={styles.hint}>{empty}</p>
      ) : (
        <ol className={styles.movers}>
          {movers.map((m) => {
            const vm = moverModel(m);
            return (
              <li key={vm.key} className={styles.mover}>
                <div className={styles.moverHead}>
                  <span>
                    <b>{vm.name}</b> <span className={styles.hint}>{vm.casinoName}</span>
                  </span>
                  <span className={cx(styles.num, toneClass(vm.tone))}>
                    {vm.changeLabel} <small>{vm.changePctLabel}</small>
                  </span>
                </div>
                <Sparkline
                  points={vm.spark}
                  runs={vm.sparkRuns}
                  marks={vm.sparkMarks}
                  single={vm.sparkSingle}
                  label={`${vm.name}'s balance line`}
                />
                <SparkLegend items={vm.sparkLegend} />
                {vm.highlights.length > 0 && (
                  <ul className={styles.moverHighlights} aria-label="Highlights">
                    {vm.highlights.map((h) => (
                      <li key={h.key} className={styles.moverHighlight}>
                        <span className={styles.moverHighlightLabel}>{h.label}</span>
                        <span className={cx(styles.moverHighlightValue, toneClass(h.tone === 'win' ? 'up' : h.tone === 'loss' ? 'down' : 'flat'))}>
                          {h.value}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className={styles.moverFoot}>
                  <span>{vm.chipsLabel} chips</span>
                  <span>vol {vm.volumeLabel}</span>
                  <span>drawdown {vm.drawdownLabel}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

function PrintList({ title, prints, empty }: { title: string; prints: AdminMarketPrint[]; empty: string }) {
  return (
    <section className={styles.panel} aria-label={title}>
      <header className={styles.panelHead}>
        <h2 className={styles.panelTitle}>{title}</h2>
      </header>
      {prints.length === 0 ? (
        <p className={styles.hint}>{empty}</p>
      ) : (
        <div className={styles.scroll}>
          <table className={styles.table}>
            <tbody>
              {prints.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.memberName} <span className={styles.hint}>{p.casinoName}</span>
                  </td>
                  <td>
                    <span className={cx(styles.swatch, gameTone(p.gameType))} aria-hidden />
                    {gameLabel(p.gameType)}
                  </td>
                  <td className={styles.outcome}>{p.outcome}</td>
                  <td className={cx(styles.num, styles.hint)}>{n(p.stake)} staked</td>
                  <td className={cx(styles.num, netClass(p.net))}>{formatSignedChips(p.net)}</td>
                  <td className={styles.hint}>{timeAgo(p.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * The chip economy read like a market: supply and what the house issued,
 * breadth, the daily tape, money flow, games as sectors, movers and prints.
 * The window cuts the moving parts — flows, sectors, prints, movers — to the
 * nights it names; supply and the ledger health stay facts about right now.
 */
function MarketTab() {
  const [marketWindow, setMarketWindow] = useState<StatsWindow>('tonight');
  // Stable per window: `useLoad` refires whenever its loader changes identity.
  const load = useCallback(() => fetchAdminMarket(marketWindow), [marketWindow]);
  const { data, error } = useLoad(load);
  if (!data) return <Status error={error} loading />;
  const tables = houseResult(data.supply.playersNet);
  const flow = (kind: string) => data.flows.find((f) => f.kind === kind)?.total ?? 0;
  const breadth = breadthShares(data.breadth);
  const { drift } = data.supply;
  return (
    <>
      <div className={styles.marketHead}>
        <WindowPicker value={marketWindow} onChange={setMarketWindow} label="Market window" />
        <span className={styles.hint}>
          Flows, sectors, prints and movers read this window; supply stays live
        </span>
      </div>
      <div className={styles.tiles}>
        <Tile label="Chips in play" value={n(data.supply.chips)} note={`held by ${n(data.supply.holders)} players`}>
          <MiniLine values={data.daily.map((d) => d.supply)} label="Chips in play, last 30 days" />
        </Tile>
        <Tile
          label="House issued"
          value={n(data.supply.issued)}
          note={`buy-ins ${n(flow('buy_in'))} · rebuys ${n(flow('rebuy'))} · grants ${formatSignedChips(flow('grant'))}`}
        >
          <MiniLine values={cumulative(data.daily.map((d) => d.issued))} label="Chips issued, running, last 30 days" />
        </Tile>
        <Tile label="Tables result" value={tables.label.replace('House ', '')} tone={toneClass(tables.tone)} note="House side · held against issued">
          <MiniLine values={cumulative(data.daily.map((d) => 0 - d.playersNet))} label="House result, running, last 30 days" />
        </Tile>
        <Tile
          label="Up / down"
          value={`${n(data.breadth.up)} / ${n(data.breadth.down)}`}
          note={`players up / down on their stake · ${n(data.breadth.flat)} even`}
        >
          {breadth.total > 0 && (
            <span className={styles.breadth} role="img" aria-label={`${data.breadth.up} up, ${data.breadth.flat} even, ${data.breadth.down} down`}>
              <span className={styles.fillUp} style={{ flexGrow: breadth.up }} />
              <span className={styles.fillFlat} style={{ flexGrow: breadth.flat }} />
              <span className={styles.fillDown} style={{ flexGrow: breadth.down }} />
            </span>
          )}
        </Tile>
        <Tile
          label="Ledger"
          value={drift === 0 ? '✓ Balanced' : `⚠ ${n(drift)} off`}
          tone={drift === 0 ? styles.up : styles.down}
          note={drift === 0 ? 'Every balance matches its ledger' : 'Balances that disagree with their ledger'}
        />
      </div>

      <DayLineChart
        id="supply"
        title="Chips in play"
        hint="End of each day, every casino"
        days={data.daily}
        values={data.daily.map((d) => d.supply)}
      />

      <div className={styles.twoUp}>
        <HouseChart days={data.daily} />
        <FlowPanel market={data} />
      </div>

      <section className={styles.panel} aria-labelledby="sectors">
        <header className={styles.panelHead}>
          <h2 id="sectors" className={styles.panelTitle}>
            Games as sectors
          </h2>
          <span className={styles.hint}>Hold is what the house kept of every chip wagered</span>
        </header>
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Game</th>
                <th className={styles.num}>Hands</th>
                <th className={styles.num}>Wagered</th>
                <th className={styles.num}>Returned</th>
                <th className={styles.num}>House hold</th>
                <th className={styles.num}>Best print</th>
                <th className={styles.num}>Worst print</th>
              </tr>
            </thead>
            <tbody>
              {data.sectors.map((s) => {
                const hold = houseHold(s.playersNet, s.wagered);
                return (
                  <tr key={s.gameType}>
                    <td>
                      <span className={cx(styles.swatch, gameTone(s.gameType))} aria-hidden />
                      {gameLabel(s.gameType)}
                    </td>
                    <td className={styles.num}>{n(s.hands)}</td>
                    <td className={styles.num}>{n(s.wagered)}</td>
                    <td className={styles.num}>{n(s.returned)}</td>
                    <td className={cx(styles.num, toneClass(hold.tone))}>{hold.label}</td>
                    <td className={cx(styles.num, netClass(s.best))}>{formatSignedChips(s.best)}</td>
                    <td className={cx(styles.num, netClass(s.worst))}>{formatSignedChips(s.worst)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <div className={styles.threeUp}>
        <MoverList title="Top gainers" movers={data.gainers} empty="Nobody is up on their stake yet." />
        <MoverList title="Top losers" movers={data.losers} empty="Nobody is down on their stake yet." />
        <MoverList title="Most active" movers={data.active} empty="No chips wagered yet." />
      </div>

      <div className={styles.twoUp}>
        <PrintList title="Biggest wins" prints={data.prints.wins} empty="No winning hands yet." />
        <PrintList title="Biggest losses" prints={data.prints.losses} empty="No losing hands yet." />
      </div>
    </>
  );
}

const EMPTY_FILTER: RoundFilter ={ casinoId: '', memberId: '', memberLabel: '', gameType: '' };

/**
 * The site-wide admin dashboard: who signed up, who is playing what, every
 * settled hand, and what is live right now. Open to baked-in admins and to
 * accounts promoted from the Users tab.
 */
export function Admin() {
  const [params, setParams] = useSearchParams();
  const tab = parseTab(params.get('tab'));
  const [session, setSession] = useState<SessionUser | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [roundFilter, setRoundFilter] = useState<RoundFilter>(EMPTY_FILTER);

  useEffect(() => {
    getSession()
      .then(async (s) => {
        setSession(s);
        setAllowed(s ? await fetchIsAdmin() : false);
      })
      .catch(() => setAllowed(false));
  }, []);

  const go = (next: AdminTab) =>
    setParams(
      (current) => {
        const p = new URLSearchParams(current);
        if (next === 'overview') p.delete('tab');
        else p.set('tab', next);
        return p;
      },
      { replace: true },
    );

  if (allowed === null) {
    return (
      <main className={styles.wrap}>
        <p className={styles.hint}>Checking your session…</p>
      </main>
    );
  }

  if (!session || !allowed) {
    return (
      <main className={styles.wrap}>
        <div className={styles.door}>
          <p className={styles.kicker}>Admin</p>
          <h1 className={styles.title}>{session ? 'Admins only' : 'Sign in first'}</h1>
          <p className={styles.hint}>
            {session
              ? `${session.email} does not have admin access. Ask an admin to turn it on.`
              : 'Sign in on the casino console, then come back here.'}
          </p>
          <Link className={styles.linkish} to="/casino">
            Go to the casino console
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.wrap}>
      <header className={styles.top}>
        <div>
          <p className={styles.kicker}>Admin</p>
          <h1 className={styles.title}>
            Dashboard
            <AlphaBadge />
          </h1>
        </div>
        <div className={styles.topSide}>
          <span className={styles.hint}>{session.email}</span>
          <Link className={styles.linkish} to="/casino">
            Casino console
          </Link>
        </div>
      </header>

      <nav className={styles.tabs} aria-label="Admin navigation">
        {ADMIN_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={t.id === tab ? styles.tabOn : styles.tab}
            aria-current={t.id === tab ? 'page' : undefined}
            onClick={() => go(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className={styles.body}>
        {tab === 'overview' && <OverviewTab />}
        {tab === 'market' && <MarketTab />}
        {tab === 'players' && (
          <PlayersTab
            onHistory={(p) => {
              setRoundFilter({ ...EMPTY_FILTER, casinoId: p.casinoId, memberId: p.id, memberLabel: p.name });
              go('rounds');
            }}
          />
        )}
        {tab === 'rounds' && <RoundsTab filter={roundFilter} setFilter={setRoundFilter} />}
        {tab === 'users' && <UsersTab me={session} />}
        {tab === 'casinos' && (
          <CasinosTab
            onHistory={(c) => {
              setRoundFilter({ ...EMPTY_FILTER, casinoId: c.id });
              go('rounds');
            }}
          />
        )}
        {tab === 'live' && <LiveTab />}
      </div>
    </main>
  );
}
